import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import ProgramManagementService from '../_services/ProgramManagementService';

export interface AddCYPSubmitBody {
  programId: number;
  courseId: number;
  yearId: number;
  termId?: number | null;
  credits?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface AddCYPDialogProps {
  open: boolean;
  programs: Array<{ id: number; name: string }>;
  onSubmit: (body: AddCYPSubmitBody) => Promise<void>;
  onClose: () => void;
}

const AddCYPDialog: React.FC<AddCYPDialogProps> = ({ open, programs, onSubmit, onClose }) => {
  const [programId, setProgramId] = useState<string>('');
  const [courseId, setCourseId] = useState<string>('');
  const [yearId, setYearId] = useState<string>('');
  const [termId, setTermId] = useState<string>('');
  const [credits, setCredits] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lookups, setLookups] = useState<{
    years: Array<{ id: number; name: string }>;
    terms: Array<{ id: number; name: string; programId: number | null }>;
    courses: Array<{ id: number; name: string }>;
  }>({ years: [], terms: [], courses: [] });
  const [loadingLookups, setLoadingLookups] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProgramId('');
    setCourseId('');
    setYearId('');
    setTermId('');
    setCredits('');
    setStartDate('');
    setEndDate('');
    setError('');
    (async () => {
      setLoadingLookups(true);
      try {
        const r = await ProgramManagementService.getLookups();
        if (r.success) {
          setLookups({
            years: r.data.years || [],
            terms: r.data.terms || [],
            courses: r.data.courses || [],
          });
        }
      } finally {
        setLoadingLookups(false);
      }
    })();
  }, [open]);

  const termsForProgram = useMemo(
    () => lookups.terms.filter(t => t.programId === Number(programId)),
    [lookups.terms, programId]
  );

  useEffect(() => {
    setTermId('');
  }, [programId]);

  const handleSubmit = async () => {
    if (!programId || !courseId || !yearId) {
      setError('Program, Course and Year are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        programId: Number(programId),
        courseId: Number(courseId),
        yearId: Number(yearId),
        termId: termId ? Number(termId) : null,
        credits: credits === '' ? null : Number(credits),
        startDate: startDate || null,
        endDate: endDate || null,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to add module');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Course → Program Mapping</DialogTitle>
      <DialogContent dividers>
        {loadingLookups && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Box mb={2}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Program *</InputLabel>
              <Select
                value={programId}
                onChange={(e) => setProgramId(String(e.target.value))}
                label="Program *"
              >
                <MenuItem value=""><em>-- Select --</em></MenuItem>
                {programs.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Course *</InputLabel>
              <Select
                value={courseId}
                onChange={(e) => setCourseId(String(e.target.value))}
                label="Course *"
              >
                <MenuItem value=""><em>-- Select --</em></MenuItem>
                {lookups.courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Year *</InputLabel>
              <Select
                value={yearId}
                onChange={(e) => setYearId(String(e.target.value))}
                label="Year *"
              >
                <MenuItem value=""><em>-- Select --</em></MenuItem>
                {lookups.years.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" variant="outlined" disabled={!programId}>
              <InputLabel>Term</InputLabel>
              <Select
                value={termId}
                onChange={(e) => setTermId(String(e.target.value))}
                label="Term"
              >
                <MenuItem value=""><em>-- None --</em></MenuItem>
                {termsForProgram.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Credits"
              type="number"
              size="small"
              variant="outlined"
              fullWidth
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || !programId || !courseId || !yearId}
        >
          {saving ? <CircularProgress size={18} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCYPDialog;
